import React, { useRef, useState } from "react";

import { makeStyles } from "tss-react/mui";

import AppointmentEventBlock from "./AppointmentEventBlock";
import {
  CalendarAppointment,
  DAY_MINUTES,
  DraftSlot,
  HOUR_HEIGHT,
  SNAP_MINUTES,
  clampMinutes,
  formatMinutesRange,
  minutesOfDay,
  minutesToOffset,
  offsetToMinutes,
  snapMinutes,
} from "./geometry";

type Props = {
  date: Date;
  appointments: CalendarAppointment[];
  now: Date;
  isToday: boolean;
  onView: (id: string) => void;
  onCreate: (date: Date, startMin: number, endMin: number) => void;
  onReschedule: (
    id: string,
    date: Date,
    startMin: number,
    endMin: number
  ) => void;
  // Placeholder block for the appointment being encoded (already filtered to
  // this column's date by the grid).
  placeholder?: DraftSlot | null;
  onPlaceholderMove?: (date: Date, startMin: number, endMin: number) => void;
  // Highlighted because the placeholder is being dragged over this column.
  isDropTarget?: boolean;
  // Report which day (ISO) the pointer is over during a placeholder drag,
  // null when back over the source day.
  onHoverDate?: (iso: string | null) => void;
};

type Gesture =
  | { kind: "create"; anchorMin: number }
  | {
      kind: "move";
      id: string;
      movable: boolean;
      anchorMin: number;
      origStart: number;
      origEnd: number;
      moved: boolean;
      // ISO of the day column the pointer is currently over (cross-day drag).
      targetDate?: string;
    }
  | {
      kind: "place";
      anchorMin: number;
      origStart: number;
      origEnd: number;
      moved: boolean;
      // ISO of the day column the pointer is currently over (cross-day drag).
      targetDate?: string;
    };

const COLUMN_HEIGHT = (DAY_MINUTES / 60) * HOUR_HEIGHT;
const DEFAULT_DURATION = 60;

// Assign overlapping appointments to side-by-side columns (Google-Calendar
// style). Returns, per appointment id, which column it sits in and how many
// columns its overlap cluster spans, so a block can be sized to 1/cols width.
function packColumns(
  items: { id: string; start: number; end: number }[]
): Map<string, { col: number; cols: number }> {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const result = new Map<string, { col: number; cols: number }>();

  let cluster: { id: string; col: number }[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    const cols = colEnds.length;
    cluster.forEach((entry) => result.set(entry.id, { col: entry.col, cols }));
    cluster = [];
    colEnds = [];
    clusterEnd = -Infinity;
  };

  sorted.forEach((item) => {
    if (cluster.length > 0 && item.start >= clusterEnd) flush();

    let col = colEnds.findIndex((end) => end <= item.start);
    if (col === -1) {
      col = colEnds.length;
      colEnds.push(item.end);
    } else {
      colEnds[col] = item.end;
    }
    cluster.push({ id: item.id, col });
    clusterEnd = Math.max(clusterEnd, item.end);
  });
  flush();

  return result;
}

const AppointmentDayColumn: React.FC<Props> = ({
  date,
  appointments,
  now,
  isToday,
  onView,
  onCreate,
  onReschedule,
  placeholder,
  onPlaceholderMove,
  isDropTarget,
  onHoverDate,
}) => {
  const { classes, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const [draft, setDraft] = useState<{ start: number; end: number } | null>(
    null
  );
  // Live position of the block being dragged, so it follows the pointer without
  // mutating the underlying (Apollo-cached) appointment until drop.
  const [movePreview, setMovePreview] = useState<{
    id: string;
    start: number;
    end: number;
  } | null>(null);
  // Live position of the placeholder while it is being dragged.
  const [placePreview, setPlacePreview] = useState<{
    start: number;
    end: number;
  } | null>(null);

  const pointerMinutes = (clientY: number): number => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return snapMinutes(offsetToMinutes(clientY - rect.top));
  };

  // Which day column is under the pointer. The source column holds the
  // pointer capture, so hit-test the document for the hovered [data-date]
  // column to support dropping the placeholder onto another day.
  const dateAtPoint = (clientX: number, clientY: number): string | null => {
    const el = document
      .elementFromPoint(clientX, clientY)
      ?.closest("[data-date]");
    return el?.getAttribute("data-date") ?? null;
  };

  const capture = (event: React.PointerEvent) => {
    try {
      containerRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // Pointer may no longer be active; container handlers still drive it.
    }
  };

  const handleBackgroundPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0) return;
    const anchorMin = pointerMinutes(event.clientY);
    gestureRef.current = { kind: "create", anchorMin };
    setDraft({ start: anchorMin, end: anchorMin });
    capture(event);
  };

  const handleBlockPointerDown = (
    event: React.PointerEvent,
    appointment: CalendarAppointment
  ) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    // Only cancelled appointments are locked. Past ("finished"-looking) and
    // even explicitly finished ones stay draggable — rescheduling a missed
    // appointment is a primary use case, and the list view's Edit flow allows
    // changing their times too. The confirmation modal guards against slips.
    const movable = appointment.status !== "cancelled";
    gestureRef.current = {
      kind: "move",
      id: appointment.id,
      movable,
      anchorMin: pointerMinutes(event.clientY),
      origStart: minutesOfDay(appointment.start),
      origEnd: minutesOfDay(appointment.end),
      moved: false,
    };
    capture(event);
  };

  const handlePlaceholderPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 || !placeholder) return;
    event.stopPropagation();
    gestureRef.current = {
      kind: "place",
      anchorMin: pointerMinutes(event.clientY),
      origStart: placeholder.startMin,
      origEnd: placeholder.endMin,
      moved: false,
    };
    capture(event);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    const current = pointerMinutes(event.clientY);

    if (gesture.kind === "create") {
      setDraft({
        start: Math.min(gesture.anchorMin, current),
        end: Math.max(gesture.anchorMin, current),
      });
      return;
    }

    if (gesture.kind === "place") {
      // Track the hovered day before any vertical early-return so a purely
      // sideways drag still relocates the placeholder.
      const overDate = dateAtPoint(event.clientX, event.clientY);
      if (overDate) gesture.targetDate = overDate;
      onHoverDate?.(
        overDate && overDate !== date.toISOString() ? overDate : null
      );

      const duration = gesture.origEnd - gesture.origStart;
      const delta = current - gesture.anchorMin;
      if (Math.abs(delta) < SNAP_MINUTES && !gesture.moved) return;
      const start = clampMinutes(
        Math.min(Math.max(gesture.origStart + delta, 0), DAY_MINUTES - duration)
      );
      gesture.moved = true;
      setPlacePreview({ start, end: start + duration });
      return;
    }

    if (!gesture.movable) return;
    // Track the hovered day before the jitter early-return so a purely
    // sideways drag can reschedule onto another day.
    const overDate = dateAtPoint(event.clientX, event.clientY);
    if (overDate) gesture.targetDate = overDate;
    onHoverDate?.(
      overDate && overDate !== date.toISOString() ? overDate : null
    );
    const duration = gesture.origEnd - gesture.origStart;
    const delta = current - gesture.anchorMin;
    if (Math.abs(delta) < SNAP_MINUTES && !gesture.moved) return; // ignore jitter
    const start = clampMinutes(
      Math.min(Math.max(gesture.origStart + delta, 0), DAY_MINUTES - duration)
    );
    gesture.moved = true;
    setMovePreview({ id: gesture.id, start, end: start + duration });
  };

  const endGesture = (event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    onHoverDate?.(null);
    try {
      containerRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      // Ignore if capture was never established.
    }

    if (gesture?.kind === "place") {
      const preview = placePreview;
      setPlacePreview(null);
      const targetIso = gesture.targetDate;
      const movedToOtherDay = !!targetIso && targetIso !== date.toISOString();
      if ((gesture.moved && preview) || movedToOtherDay) {
        onPlaceholderMove?.(
          movedToOtherDay ? new Date(targetIso) : date,
          preview?.start ?? gesture.origStart,
          preview?.end ?? gesture.origEnd
        );
      }
      return;
    }

    if (gesture?.kind === "create" && draft) {
      let { start, end } = draft;
      if (end - start < SNAP_MINUTES) {
        // A tap (no meaningful drag) creates a default one-hour slot.
        start = Math.min(start, DAY_MINUTES - DEFAULT_DURATION);
        end = start + DEFAULT_DURATION;
      }
      setDraft(null);
      onCreate(date, start, end);
      return;
    }

    if (gesture?.kind === "move") {
      const preview = movePreview;
      setMovePreview(null);
      const targetIso = gesture.targetDate;
      const movedToOtherDay =
        gesture.movable && !!targetIso && targetIso !== date.toISOString();
      if ((gesture.moved && preview) || movedToOtherDay) {
        onReschedule(
          gesture.id,
          movedToOtherDay ? new Date(targetIso) : date,
          preview?.start ?? gesture.origStart,
          preview?.end ?? gesture.origEnd
        );
      } else {
        // A press without a real drag is a click → open details.
        onView(gesture.id);
      }
      return;
    }

    setDraft(null);
  };

  const layout = packColumns(
    appointments.map((appointment) => ({
      id: appointment.id,
      start: minutesOfDay(appointment.start),
      end: minutesOfDay(appointment.end),
    }))
  );

  const placeStart = placePreview?.start ?? placeholder?.startMin ?? 0;
  const placeEnd = Math.max(
    placePreview?.end ?? placeholder?.endMin ?? 0,
    placeStart + SNAP_MINUTES
  );

  return (
    <div
      ref={containerRef}
      data-date={date.toISOString()}
      className={cx(
        classes.column,
        isToday && classes.today,
        isDropTarget && classes.dropTarget
      )}
      style={{ height: COLUMN_HEIGHT }}
      onPointerDown={handleBackgroundPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      {appointments.map((appointment) => {
        const place = layout.get(appointment.id) ?? { col: 0, cols: 1 };
        const preview = movePreview?.id === appointment.id ? movePreview : null;
        const startMin = preview
          ? preview.start
          : minutesOfDay(appointment.start);
        const endMin = preview ? preview.end : minutesOfDay(appointment.end);
        return (
          <AppointmentEventBlock
            key={appointment.id}
            appointment={appointment}
            now={now}
            topPx={minutesToOffset(startMin)}
            heightPx={minutesToOffset(
              Math.max(endMin - startMin, SNAP_MINUTES)
            )}
            leftPct={(place.col / place.cols) * 100}
            widthPct={(1 / place.cols) * 100}
            dragging={!!preview}
            onGestureStart={(event) =>
              handleBlockPointerDown(event, appointment)
            }
          />
        );
      })}

      {draft && draft.end > draft.start && (
        <div
          className={classes.draft}
          style={{
            top: minutesToOffset(draft.start),
            height: minutesToOffset(draft.end - draft.start),
          }}
        />
      )}

      {placeholder && (
        <div
          className={cx(
            classes.placeholder,
            placePreview && classes.placeholderDragging
          )}
          style={{
            top: minutesToOffset(placeStart),
            height: minutesToOffset(placeEnd - placeStart),
          }}
          onPointerDown={handlePlaceholderPointerDown}
        >
          <span className={classes.placeholderLabel}>
            New appointment · {formatMinutesRange(placeStart, placeEnd)}
          </span>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  column: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    borderRight: "1px solid #DEE6F0",
    backgroundImage: "linear-gradient(#EDF0F5 1px, transparent 1px)",
    backgroundSize: `100% ${HOUR_HEIGHT}px`,
    touchAction: "none",
  },
  today: {
    backgroundColor: "rgba(61,109,240,0.04)",
  },
  draft: {
    position: "absolute",
    left: 2,
    right: 2,
    borderRadius: 6,
    backgroundColor: "rgba(61,109,240,0.2)",
    border: "1px dashed #3D6DF0",
    pointerEvents: "none",
  },
  dropTarget: {
    backgroundColor: "rgba(61,109,240,0.1)",
    boxShadow: "inset 0 0 0 2px #3D6DF0",
  },
  placeholder: {
    position: "absolute",
    left: 2,
    right: 2,
    borderRadius: 6,
    backgroundColor: "rgba(61,109,240,0.25)",
    border: "1px dashed #3D6DF0",
    zIndex: 3,
    cursor: "grab",
    padding: "2px 6px",
    overflow: "hidden",
    touchAction: "none",
  },
  placeholderDragging: {
    cursor: "grabbing",
    opacity: 0.85,
    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
  },
  placeholderLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1D3FA8",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  },
}));

export default AppointmentDayColumn;
